import { useEffect, useState } from "react";
import { Instagram as IgIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface InstagramProfileImageProps {
  src?: string | null;
  alt: string;
  className?: string;
  fallbackClassName?: string;
  iconClassName?: string;
}

// Instagram CDN URLs are signed and block hotlinking from browsers.
// Always route through an image proxy. weserv.nl needs the URL without protocol,
// and the full querystring must be preserved (encoded as a single param value).
const toProxyUrl = (url: string) => {
  const stripped = url.replace(/^https?:\/\//, "");
  return `https://wsrv.nl/?url=${encodeURIComponent(stripped)}&default=1&n=-1`;
};

// Fallback proxy if weserv fails
const toProxyUrlAlt = (url: string) =>
  `https://images.weserv.nl/?url=${encodeURIComponent(url.replace(/^https?:\/\//, ""))}`;

export const InstagramProfileImage = ({
  src,
  alt,
  className,
  fallbackClassName,
  iconClassName,
}: InstagramProfileImageProps) => {
  const initial = src ? toProxyUrl(src) : "";
  const [currentSrc, setCurrentSrc] = useState(initial);
  const [stage, setStage] = useState<"proxy" | "alt" | "direct" | "failed">(
    src ? "proxy" : "failed",
  );

  useEffect(() => {
    if (src) {
      setCurrentSrc(toProxyUrl(src));
      setStage("proxy");
    } else {
      setCurrentSrc("");
      setStage("failed");
    }
  }, [src]);

  return (
    <Avatar className={cn("bg-muted", className)}>
      {stage !== "failed" && currentSrc ? (
        <AvatarImage
          src={currentSrc}
          alt={alt}
          referrerPolicy="no-referrer"
          className="object-cover"
          onError={() => {
            if (!src) {
              setStage("failed");
              return;
            }
            if (stage === "proxy") {
              setCurrentSrc(toProxyUrlAlt(src));
              setStage("alt");
              return;
            }
            if (stage === "alt") {
              setCurrentSrc(src);
              setStage("direct");
              return;
            }
            setStage("failed");
          }}
        />
      ) : null}
      <AvatarFallback className={cn("bg-muted text-muted-foreground", fallbackClassName)}>
        <IgIcon className={cn("h-1/2 w-1/2", iconClassName)} />
      </AvatarFallback>
    </Avatar>
  );
};
